import { z } from "zod";
import { COMMISSION_BASES, MAX_COMMISSION_PCT4, MAX_DISCOUNT_PCT4, type CommissionLevel } from "@cx/domain";
import { contactInput } from "./contacts";
import { ulidParam } from "./ids";

// ===== Vendedores (RF-CAD-05) =====

const commissionPct = z
  .number("Informe o percentual")
  .int("Percentual inválido")
  .min(0, "A comissão deve ficar entre 0% e 100%")
  .max(MAX_COMMISSION_PCT4, "A comissão deve ficar entre 0% e 100%");

/** Dados de vendedor do contato: comissão padrão, base da liberação, permissões e situação. */
export const sellerInput = z.object({
  defaultCommissionPct4: commissionPct.default(0),
  /** Faturado: comissão liberada ao faturar; recebido: proporcional a cada baixa do título (05 §6). */
  commissionBase: z.enum(COMMISSION_BASES, "Base inválida").default("invoiced"),
  canAddUnregisteredItems: z.boolean().default(false),
  canViewStock: z.boolean().default(true),
  canEditCreditLimit: z.boolean().default(false),
  status: z.enum(["active", "inactive"], "Situação inválida").default("active"),
});
export type SellerInput = z.output<typeof sellerInput>;
export type SellerFormValues = z.input<typeof sellerInput>;

/** Novo vendedor: um contato existente (`contactId`) ou um contato novo (`contact`), nunca os dois. */
export const sellerCreateInput = sellerInput
  .extend({
    contactId: ulidParam.optional(),
    contact: contactInput.optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.contactId === !v.contact)
      ctx.addIssue({
        code: "custom",
        path: ["contactId"],
        message: "Escolha um contato existente ou cadastre um novo",
      });
  });
export type SellerCreateInput = z.output<typeof sellerCreateInput>;

export const sellerListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(["active", "inactive"]).optional(),
});
export type SellerListQuery = z.output<typeof sellerListQuery>;

/** Usuário de acesso vinculado (vem do D1; só para quem tem users:read). */
export type SellerUser = { id: string; name: string; email: string };

export type SellerView = SellerInput & {
  id: string;
  name: string;
  document: string | null;
  email: string | null;
  contactStatus: "active" | "inactive" | "no_movement";
  /** Ausente sem users:read; null = sem usuário vinculado. */
  user?: SellerUser | null;
};

/** Vendedor ativo para escolher no contato (sem comissão). */
export type SellerOption = { id: string; name: string };

/** Vínculo do usuário de acesso (usuário-vendedor); null desvincula. */
export const sellerUserInput = z.object({ userId: ulidParam.nullable() });
export type SellerUserInput = z.output<typeof sellerUserInput>;

/** Membro do tenant para o vínculo. `sellerId` = vendedor ao qual já está ligado. */
export type TenantMemberView = {
  id: string;
  name: string;
  email: string;
  userType: "normal" | "seller" | "accountant" | "api";
  sellerId: string | null;
};

// ===== Regras de comissão =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const discount = z
  .number("Percentual inválido")
  .int("Percentual inválido")
  .min(0, "A faixa de desconto vai de 0% a 100%")
  .max(MAX_DISCOUNT_PCT4, "A faixa de desconto vai de 0% a 100%");

/**
 * Regra de comissão (05 §6). `sellerId` null = todos os vendedores (exige produto: a regra geral só de faixa fica
 * fora da precedência). Categoria entra com o cadastro de categorias.
 */
export const commissionRuleInput = z
  .object({
    sellerId: nullish(ulidParam),
    productId: nullish(ulidParam),
    discountFromPct4: nullish(discount),
    discountToPct4: nullish(discount),
    commissionPct4: commissionPct,
    priority: z.number().int("Prioridade inválida").min(-1000).max(1000).default(0),
  })
  .superRefine((v, ctx) => {
    // Os limites de cada ponta já vêm dos campos; aqui só a ordem (o Zod 4 roda o refinamento mesmo assim).
    if (v.discountFromPct4 !== null && v.discountToPct4 !== null && v.discountFromPct4 > v.discountToPct4)
      ctx.addIssue({
        code: "custom",
        path: ["discountToPct4"],
        message: "O início da faixa deve ser menor ou igual ao fim",
      });
    const hasBand = v.discountFromPct4 !== null || v.discountToPct4 !== null;
    if (v.sellerId === null && v.productId === null)
      ctx.addIssue({
        code: "custom",
        path: ["productId"],
        message: "Regra para todos os vendedores precisa de um produto",
      });
    else if (v.productId === null && !hasBand)
      ctx.addIssue({
        code: "custom",
        path: ["productId"],
        message: "Informe o produto ou a faixa de desconto",
      });
  });
export type CommissionRuleInput = z.output<typeof commissionRuleInput>;
export type CommissionRuleFormValues = z.input<typeof commissionRuleInput>;

export const commissionRuleListQuery = z
  .object({
    /** Regras de um vendedor. */
    sellerId: ulidParam.optional(),
    /** "general" = só as regras para todos os vendedores. */
    scope: z.enum(["general"]).optional(),
  })
  .refine((v) => !(v.sellerId && v.scope), {
    path: ["scope"],
    message: "Use sellerId ou scope, não os dois",
  });
export type CommissionRuleListQuery = z.output<typeof commissionRuleListQuery>;

export type CommissionRuleView = CommissionRuleInput & {
  id: string;
  sellerName: string | null;
  productName: string | null;
  sku: string | null;
};

/** Simulação da comissão do vendedor para um produto e um desconto (05 §6). */
export const commissionPreviewQuery = z.object({
  productId: ulidParam,
  discountPct4: z.coerce
    .number()
    .int("Percentual inválido")
    .min(0, "O desconto vai de 0% a 100%")
    .max(MAX_DISCOUNT_PCT4, "O desconto vai de 0% a 100%")
    .default(0),
});
export type CommissionPreviewQuery = z.output<typeof commissionPreviewQuery>;

export type CommissionPreview = {
  sellerId: string;
  productId: string;
  discountPct4: number;
  pct4: number;
  source: "rule" | "default";
  level: CommissionLevel;
  ruleId: string | null;
};
